// Searchable settings browser (player side).
//
// Uses the same registry as the admin defaults editor, but edits the user's
// Settings object. Applying mirrors the value into the real page control and
// dispatches its native event, so the existing menu handlers remain the single
// apply path. Role filtering uses the same access ladder as the rest of the UI.

import type { Role } from "../admin";
import { canSeeSetting } from "../settings/access";
import {
  accessFor,
  atDefault,
  clearExplicit,
  effectiveValue,
  loadCachedGlobalDefaults,
  markExplicit,
  resetSetting,
} from "../settings/defaults";
import {
  SETTINGS,
  categoryLabel,
  formatDefault,
  formatSettingValue,
  getSettingValue,
  setSettingValue,
  type SettingDef,
} from "../settings/registry";
import { searchSettings } from "../settings/search";
import { MODE_PRESETS } from "../modes";
import { PRESETS } from "../rules";
import { dsElement } from "./daisy";

export interface SettingsSearchCallbacks {
  getRole: () => Role;
  getSettings: () => unknown;
  /** Controls on the real pages for this setting (including hidden pages). */
  findControls: (id: string) => HTMLElement[];
  /** Persist + apply a value when there is no page control to drive. */
  commit: () => void;
  /** Switch to the page that owns a structured editor. */
  openGroup: (id: string) => void;
  /** Open the page/section that owns this setting and highlight it. */
  navigate: (def: SettingDef) => void;
  /** Run a console command line (vote actions). */
  onCommand: (line: string) => void;
  /** Current server family, used to filter mode suggestions. */
  getServerFamily: () => "race" | "combat" | null;
  /** Map catalog matches for `vote map <prefix>` (empty while loading). */
  mapSuggestions: (query: string) => Array<{ name: string; detail: string }>;
  /** Load the map catalog; render() runs again when it arrives. */
  ensureMapSuggestions: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** A runnable action shown above settings results (currently votes). */
interface SearchAction {
  label: string;
  detail: string;
  /** Console line to run (the console is the single executor). */
  line: string;
}

interface VoteActionContext {
  /** Current server family; filters mode suggestions when known. */
  family: "race" | "combat" | null;
  /** Cached map catalog matches (empty when the catalog is not loaded yet). */
  mapSuggestions: (query: string) => Array<{ name: string; detail: string }>;
  /** Ask the menu to load the map catalog; it re-renders when it arrives. */
  ensureMaps: () => void;
}

/**
 * Vote actions for the settings search bar. Typing `vote`, `vote map`,
 * `vote random slick`, `vote mode`, `vote freestyle`… surfaces what can be run;
 * the actual work goes through `consoleExec` so there is one command path.
 * Mode suggestions follow the current server family (race/freestyle vs combat),
 * and map names are completed from the map catalog as you type.
 */
function voteActions(query: string, ctx: VoteActionContext): SearchAction[] {
  const raw = query.trim();
  const s = raw.toLowerCase();
  if (!s) return [];
  const isVote =
    s === "vote" ||
    s.startsWith("vote ") ||
    s.startsWith("map ") ||
    s === "random" ||
    s.startsWith("random ");
  if (!isVote) return [];
  const out: SearchAction[] = [];
  const add = (label: string, detail: string, line: string) =>
    out.push({ label, detail, line });

  const modes = MODE_PRESETS.filter((p) => {
    if (!ctx.family) return true;
    const isRace = p.mode.structure === "race";
    return ctx.family === "race" ? isRace : !isRace;
  });
  const addMode = (p: (typeof modes)[number]) =>
    add(`Vote mode: ${p.label}`, p.id, `vote mode ${p.id}`);
  const matchModes = (text: string) => {
    const t = text.toLowerCase().trim();
    if (!t) return [];
    const exact = modes.filter(
      (p) => p.id.toLowerCase() === t || p.label.toLowerCase() === t,
    );
    return exact.length
      ? exact
      : modes.filter(
          (p) => p.id.toLowerCase().startsWith(t) || p.label.toLowerCase().startsWith(t),
        );
  };
  const addMaps = (prefix: string) => {
    const maps = ctx.mapSuggestions(prefix);
    if (maps.length > 0) {
      for (const m of maps.slice(0, 8)) {
        add(`Vote map: ${m.name}`, m.detail || "start a map vote", `vote map ${m.name}`);
      }
      return;
    }
    ctx.ensureMaps();
    add(`Vote for “${prefix}”`, "start a map vote", `vote map ${prefix}`);
  };

  const rest = s.startsWith("vote") ? raw.slice(4).trim() : raw;
  const r = rest.toLowerCase();

  if (!r) {
    add("Vote for a random map", "start a map vote with a random map", "vote map random");
    add("Vote yes", "approve the current vote", "vote yes");
    add("Vote no", "reject the current vote", "vote no");
    return out;
  }
  if (r === "yes" || r === "no") {
    add(
      `Vote ${r}`,
      r === "yes" ? "approve the current vote" : "reject the current vote",
      `vote ${r}`,
    );
    return out;
  }
  if (r === "collision" || r.startsWith("collision ")) {
    const arg = r.slice("collision".length).trim();
    for (const value of ["on", "off"]) {
      if (arg && arg !== value) continue;
      add(
        `Vote collision ${value}`,
        `turn player-vs-player collision ${value}`,
        `vote collision ${value}`,
      );
    }
    return out;
  }
  if (r === "mode" || r.startsWith("mode ")) {
    const partial = rest.slice(4).trim();
    const hits = partial ? matchModes(partial) : modes;
    for (const p of hits.slice(0, 12)) addMode(p);
    if (hits.length === 0) {
      add(`Vote mode “${partial}”`, "apply a mode preset and propose it", `vote mode ${partial}`);
    }
    return out;
  }
  if (r === "rules" || r.startsWith("rules ")) {
    const partial = rest.slice(5).trim();
    if (partial) {
      add(`Vote rules “${partial}”`, "apply a rules preset and propose it", `vote rules ${partial}`);
      return out;
    }
    for (const p of PRESETS) {
      add(`Vote rules: ${p.label}`, p.id, `vote rules ${p.id}`);
    }
    return out;
  }
  const randomSpec = (spec: string): boolean => {
    const m = /^random(?:\s+(.+))?$/i.exec(spec.trim());
    if (!m) return false;
    const keyword = (m[1] ?? "").trim();
    add(
      keyword ? `Vote for a random ${keyword} map` : "Vote for a random map",
      "pick a random map from the catalog",
      keyword ? `vote map random ${keyword}` : "vote map random",
    );
    return true;
  };
  if (r === "map" || r.startsWith("map ")) {
    const spec = r === "map" ? "" : rest.slice(4).trim();
    if (!spec) {
      add("Vote for a random map", "pick a random map from the catalog", "vote map random");
    } else if (!randomSpec(spec)) {
      addMaps(spec);
    }
    return out;
  }
  if (r === "random" || r.startsWith("random ")) {
    randomSpec(rest);
    return out;
  }

  // A bare word can be a mode name (`vote freestyle`) before it is a map name.
  const modeHits = matchModes(rest);
  if (modeHits.length > 0) {
    for (const p of modeHits.slice(0, 10)) addMode(p);
    return out;
  }
  const unavailable = MODE_PRESETS.filter(
    (p) =>
      p.id.toLowerCase().startsWith(r) || p.label.toLowerCase().startsWith(r),
  );
  if (unavailable.length > 0) {
    for (const p of unavailable.slice(0, 6)) {
      add(
        `Mode unavailable: ${p.label}`,
        "not available on this server",
        `vote mode ${p.id}`,
      );
    }
    return out;
  }
  addMaps(rest);
  return out;
}

export class SettingsSearch {
  private outside: AbortController | null = null;
  private open = false;
  /** Rendered rows in visual order + their Enter/click activation. */
  private rows: HTMLElement[] = [];
  private activations: Array<() => void> = [];
  private selected = 0;

  constructor(
    private readonly input: HTMLInputElement,
    private readonly hint: HTMLElement,
    private readonly results: HTMLElement,
    private readonly cb: SettingsSearchCallbacks,
  ) {
    this.input.addEventListener("input", () => this.render());
    this.input.addEventListener("focus", () => this.render());
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        this.close();
        this.input.blur();
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (this.rows.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        const dir = e.key === "ArrowDown" ? 1 : -1;
        this.selected = (this.selected + dir + this.rows.length) % this.rows.length;
        this.paintSelection();
        return;
      }
      if (e.key === "Enter") {
        if (this.rows.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        this.activations[this.selected]?.();
      }
    });
    this.outside = new AbortController();
    document.addEventListener(
      "pointerdown",
      (e) => {
        if (!this.open) return;
        const t = e.target as Node;
        if (this.results.contains(t) || this.input.contains(t)) return;
        this.close();
      },
      { signal: this.outside.signal },
    );
  }

  close(): void {
    this.open = false;
    this.results.hidden = true;
    this.hint.textContent = "";
    this.rows = [];
    this.activations = [];
    this.selected = 0;
  }

  /** Highlight the keyboard-selected row and keep it in view. */
  private paintSelection(): void {
    this.rows.forEach((row, i) =>
      row.classList.toggle("is-selected", i === this.selected),
    );
    this.rows[this.selected]?.scrollIntoView({ block: "nearest" });
  }

  /** Re-render if the panel is open (after a reset or a role change). */
  refresh(): void {
    if (this.open) this.render();
  }

  private visible(def: SettingDef): boolean {
    return canSeeSetting(accessFor(def, loadCachedGlobalDefaults()), this.cb.getRole());
  }

  private read(def: SettingDef): unknown {
    if (def.kind === "group") return effectiveValue(def);
    return getSettingValue(def, this.cb.getSettings());
  }

  private apply(def: SettingDef, value: unknown): void {
    if (def.kind === "group") return;
    const controls = this.cb.findControls(def.id);
    if (controls.length > 0) {
      for (const c of controls) this.setControl(c, value);
      this.dispatch(controls[0]);
    } else if (def.path || def.set) {
      setSettingValue(def, this.cb.getSettings(), value);
      markExplicit(def.id);
      this.cb.commit();
    }
  }

  private reset(def: SettingDef): void {
    const doc = loadCachedGlobalDefaults();
    resetSetting(this.cb.getSettings(), def, doc);
    const controls = this.cb.findControls(def.id);
    if (controls.length > 0) {
      const value = effectiveValue(def, doc);
      for (const c of controls) this.setControl(c, value);
      this.dispatch(controls[0]);
    } else {
      this.cb.commit();
    }
    // The dispatch marks the setting explicit; reset owns it again.
    clearExplicit(def.id);
    this.render();
  }

  private setControl(control: HTMLElement, value: unknown): void {
    if (control instanceof HTMLInputElement) {
      if (control.type === "checkbox" || control.type === "radio") control.checked = Boolean(value);
      else control.value = String(value ?? "");
      return;
    }
    if (control instanceof HTMLSelectElement) control.value = String(value ?? "");
  }

  private dispatch(control: HTMLElement): void {
    const event =
      control instanceof HTMLInputElement && (control.type === "checkbox" || control.type === "radio")
        ? new Event("change", { bubbles: true })
        : control instanceof HTMLSelectElement
          ? new Event("change", { bubbles: true })
          : new Event("input", { bubbles: true });
    control.dispatchEvent(event);
  }

  private controlFor(def: SettingDef): HTMLElement {
    const wrap = el("div", "settings-search-control");
    const value = this.read(def);
    if (def.kind === "group" || def.kind === "bind" || def.id.startsWith("sounds.")) {
      const label =
        def.kind === "bind" || def.id.startsWith("sounds.")
          ? formatSettingValue(def, value)
          : "Structured editor";
      wrap.append(el("span", "settings-search-bind", label));
      const open = el("button", "btn btn-outline btn-xs", "Open editor");
      dsElement(open, "button", { style: "outline", size: "xs" });
      open.addEventListener("click", (e) => {
        e.preventDefault();
        this.close();
        this.cb.openGroup(
          def.kind === "bind" ? "controls.binds" : def.id.startsWith("sounds.") ? "weapons.sounds" : def.id,
        );
      });
      wrap.append(open);
      return wrap;
    }
    if (def.kind === "toggle") {
      const box = el("input", "toggle toggle-primary toggle-sm");
      box.type = "checkbox";
      box.checked = Boolean(value);
      box.addEventListener("change", () => this.apply(def, box.checked));
      wrap.append(box);
      return wrap;
    }
    if (def.kind === "select") {
      const select = el("select", "select select-sm");
      for (const option of def.options ?? []) {
        const opt = el("option", "", option.label);
        opt.value = option.value;
        select.append(opt);
      }
      select.value = String(value);
      select.addEventListener("change", () => {
        this.apply(def, typeof def.shipped === "number" ? Number(select.value) : select.value);
      });
      wrap.append(select);
      return wrap;
    }
    if (def.kind === "color") {
      const color = el("input");
      color.type = "color";
      color.value = String(value ?? "#000000");
      color.addEventListener("input", () => this.apply(def, color.value));
      wrap.append(color);
      return wrap;
    }
    if (def.kind === "text") {
      const input = el("input", "input input-sm");
      input.value = String(value ?? "");
      input.addEventListener("input", () => this.apply(def, input.value));
      wrap.append(input);
      return wrap;
    }
    // number
    const n = Number(value);
    const range = el("input", "range range-primary range-xs");
    range.type = "range";
    range.min = String(def.min ?? 0);
    range.max = String(def.max ?? 100);
    range.step = String(def.step ?? 1);
    range.value = String(n);
    const num = el("input", "input input-sm");
    num.type = "number";
    num.min = range.min;
    num.max = range.max;
    num.step = range.step;
    num.value = String(n);
    const push = (v: number) => {
      if (!Number.isFinite(v)) return;
      const clamped = Math.min(Number(range.max), Math.max(Number(range.min), v));
      range.value = String(clamped);
      num.value = String(clamped);
      this.apply(def, clamped);
    };
    range.addEventListener("input", () => push(Number(range.value)));
    num.addEventListener("change", () => push(Number(num.value)));
    wrap.append(range, num);
    return wrap;
  }

  private render(): void {
    const raw = this.input.value.trim();
    const q = raw.toLowerCase();
    if (!q) {
      this.open = false;
      this.rows = [];
      this.activations = [];
      this.selected = 0;
      this.results.hidden = true;
      this.hint.textContent = "";
      return;
    }
    const defs = searchSettings(
      SETTINGS.filter((def) => this.visible(def)),
      q,
    );
    const actions = voteActions(raw, {
      family: this.cb.getServerFamily(),
      mapSuggestions: (q) => this.cb.mapSuggestions(q),
      ensureMaps: () => this.cb.ensureMapSuggestions(),
    });
    // A vote query is a command, not a settings search: keep the panel focused.
    const voteOnly =
      q === "vote" ||
      q.startsWith("vote ") ||
      q === "random" ||
      q.startsWith("random ") ||
      q.startsWith("map ");
    const shownDefs = voteOnly ? [] : defs;
    this.open = true;
    this.rows = [];
    this.activations = [];
    this.selected = 0;
    this.results.hidden = false;
    this.results.innerHTML = "";
    const total = shownDefs.length + actions.length;
    this.hint.textContent =
      `${total} match${total === 1 ? "" : "es"}` +
      (actions.length ? ` · ${actions.length} vote${actions.length === 1 ? "" : "s"}` : "");
    if (actions.length > 0) {
      this.results.append(el("div", "settings-search-group", "Vote"));
      for (const action of actions) {
        const row = el("div", "settings-search-row settings-search-action");
        const label = el("div", "settings-search-label");
        label.append(el("b", "", action.label), el("small", "", action.detail));
        row.append(label);
        const run = el("button", "btn btn-primary btn-xs", "Run");
        dsElement(run, "button", { style: "primary", size: "xs" });
        run.addEventListener("click", (e) => {
          e.preventDefault();
          this.close();
          this.cb.onCommand(action.line);
        });
        row.append(run);
        this.results.append(row);
        this.rows.push(row);
        this.activations.push(() => {
          this.close();
          this.cb.onCommand(action.line);
        });
      }
    }
    if (shownDefs.length === 0) {
      if (actions.length === 0) {
        this.results.append(
          el(
            "div",
            "settings-search-empty",
            "No visible settings match. Try a simpler word, e.g. “trail”, “volume” or “crosshair”.",
          ),
        );
      }
      this.paintSelection();
      return;
    }
    const paths = [...new Set(shownDefs.map((def) => def.category.join(".")))];
    for (const path of paths) {
      const groupDefs = shownDefs.filter((d) => d.category.join(".") === path);
      const header = el("button", "settings-search-group", categoryLabel(path.split(".")));
      header.type = "button";
      header.title = "Open this section";
      header.addEventListener("click", (e) => {
        e.preventDefault();
        const first = groupDefs[0];
        if (!first) return;
        this.close();
        this.cb.navigate(first);
      });
      this.results.append(header);
      for (const def of groupDefs) {
        const row = el("div", "settings-search-row");
        row.title = "Click to open this setting";
        row.addEventListener("click", (e) => {
          const target = e.target as HTMLElement;
          if (target.closest("input, select, button, .settings-search-control")) return;
          this.close();
          this.cb.navigate(def);
        });
        const label = el("div", "settings-search-label");
        const meta = [def.help, `default ${formatDefault(def)}`].filter(Boolean).join(" · ");
        label.append(el("b", "", def.label), el("small", "", meta));
        label.title = `${def.help ?? ""}\nDefault: ${formatDefault(def)}`;
        row.append(label, this.controlFor(def));
        const reset = el("button", "btn btn-ghost btn-xs btn-square", "\u21ba");
        reset.title = `reset ${def.label} to default (${formatDefault(def)})`;
        dsElement(reset, "button", { style: "ghost", size: "xs", shape: "square" });
        reset.toggleAttribute("data-changed", !atDefault(this.cb.getSettings(), def));
        reset.addEventListener("click", (e) => {
          e.preventDefault();
          this.reset(def);
        });
        row.append(reset);
        this.results.append(row);
        this.rows.push(row);
        this.activations.push(() => {
          this.close();
          this.cb.navigate(def);
        });
      }
    }
    this.paintSelection();
  }
}
